import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Card, CardContent, 
  Box, Avatar, IconButton, Collapse, Button, Paper, Fab 
} from '@mui/material';
import { getInstitutionVehicleMapping, addParents, getParents } from './action.ts';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Heading from '../../Components/heading.tsx';
import AddParentModal from './add-parent-modal.tsx'; 
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import FooterLogo from '../../Components/footer.tsx';

const styles = {
  container: { display: 'flex', flexDirection: 'column' as const, height: '100vh' },
  header: { marginTop: '30px', marginBottom: '0px' },
  mapContainer: { flex: 1, borderRadius: '8px', overflow: 'hidden', width: '100%', minHeight: '600px', position: 'relative' as const },
  loaderContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }
};

interface Parent {
  id: number;
  name: string;
  phone: string;
  studentName?: string;
  address?: string;
  pincode?: string;
  isRegistered: boolean;
}

const ParentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vehicleId = searchParams.get('vehicle_id') || '';
  const userId = searchParams.get('user_id') || '';
  const token = searchParams.get('token') || '';
  const userName = searchParams.get('user_name') || '';
  const vehicleNumber = searchParams.get('vehicle_number') || '';
  const [parentsData, setParentsData] = useState<Parent[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [institutionVehicleId, setInstitutionVehicleId] = useState<number | null>(null);
  const [deviceContacts, setDeviceContacts] = useState<{name: string, phone: string}[]>([]);
  const [expandedParents, setExpandedParents] = useState<number[]>([]);

  useEffect(() => {
    (window as any).receiveDeviceContacts = (contacts: any[]) => {
      if (contacts && contacts.length > 0) {
        setDeviceContacts(contacts);
      }
    };

    return () => {
      delete (window as any).receiveDeviceContacts;
    };
  }, []);

  const handleOpenModal = () => {
    setOpenModal(true);
    if ((window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(JSON.stringify({ type: 'FETCH_CONTACTS' }));
    } else {
      console.error("Bridge not found! Are you on a browser instead of the app?");
    }
  };

  const fetchParentsData = (resolvedInstVehicleId: number | string) => {
    getParents(
      resolvedInstVehicleId,
      100,
      0,
      (res: any) => {
        const dataList = res?.data || res || [];
        const formattedParents = dataList.map((parent: any) => {
          const parentName = parent.parent_name || parent.name || parent.first_name;
          const isRegistered = parent.registered === true;

          let extractedStudentName = "";
          if (Array.isArray(parent.student_names)) {
            extractedStudentName = parent.student_names
              .map((student: any) => student.name)
              .filter((name: string) => name && name.trim() !== "")
              .join(", ");
          }

          return {
            id: parent.id,
            name: parentName || 'Not Registered',
            phone: parent.recipient_phone || parent.phone_number || parent.mobile || 'N/A',
            studentName: extractedStudentName,
            address: parent.address,
            pincode: parent.pincode || parent.pin_code,
            isRegistered: isRegistered
          };
        });

        setParentsData(formattedParents);
      },
      (err: any) => {
        console.error("Failed to fetch parents", err);
        toast.error("Failed to load parents list.");
      }
    );
  };

  useEffect(() => {
    if (vehicleId) {
      getInstitutionVehicleMapping(
        [Number(vehicleId)],
        '',                  
        10,                
        0,                  
        (res: any) => {
          const mappings = res?.data || [];
          let currentInstVehicleId: number | string = '';

          if (mappings && mappings.length > 0) {
            const mapping = mappings.find((m: any) => String(m.vehicle_id) === String(vehicleId)) || mappings[0];
            if (mapping && mapping.id) {
              setInstitutionVehicleId(mapping.id);
              currentInstVehicleId = mapping.id;
            }
          }
          fetchParentsData(currentInstVehicleId);
        },
        (err: any) => {
          console.error("Failed to fetch institution vehicle mapping", err);
          toast.error("Failed to load vehicle mapping data.");
        }
      );
    }
  }, [vehicleId]);

  const handleBack = () => {
    navigate(`/wv/dashboard?user_name=${userName}&vehicle_id=${vehicleId}&user_id=${userId}&token=${token}`);
  };

  const handleAddParent = (phone: string) => {
    if (!institutionVehicleId) {
      toast.error("Institution mapping not found for this vehicle. Cannot add parent.");
      return;
    }

    addParents(
      phone, 
      institutionVehicleId,
      (response: any) => {
        toast.success("Parent added successfully");
        setOpenModal(false);
        fetchParentsData(institutionVehicleId);
      },
      (error: any) => {
        toast.error(error?.message || error?.error || "Failed to add parent");
      }
    );
  };

  const handleToggleExpand = (id: number) => {
    setExpandedParents((prev) => 
      prev.includes(id) ? prev.filter((parentId) => parentId !== id) : [...prev, id]
    );
  };

  return (
    <Box sx={{ background: 'linear-gradient(45deg, black, rgb(237, 27, 27))', minHeight: '100vh', pt: 0, pb: 12 }}>
      
      <Box sx={{ bgcolor: '#ffffff', pb: 2, borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <Container maxWidth="xs" sx={{ padding: 0 }}>
          <Box style={styles.header}>
            <Heading 
              title="List of Parents" 
              onBackClick={handleBack} 
              vehicle={vehicleNumber}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xs" sx={{ padding: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', p: 1, mt: 1 }}>
          {parentsData.length === 0 ? (
            <Typography variant="body1" align="center" color="white" sx={{ mt: 4 }}>
              No parents found.
            </Typography>
          ) : (
            parentsData.map((parent) => {
              const hasDetails = parent.studentName || parent.address || parent.pincode;
              const isExpanded = expandedParents.includes(parent.id);

              return (
                <Card 
                  key={parent.id} 
                  sx={{ 
                    borderRadius: '12px', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: '1px solid #e0e0e0',
                    overflow: 'visible',
                    bgcolor: '#ffffff'
                  }}
                >
                  <CardContent sx={{ pb: '12px !important', paddingTop: '12px !important' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={1.5} flex={1} overflow="hidden">
                        <Avatar sx={{ bgcolor: 'rgba(0, 77, 102, 0.1)', color: '#004d66', width: 36, height: 36 }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>

                        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} sx={{ overflow: 'hidden' }}>
                          <Typography 
                            variant="subtitle1" 
                            fontWeight="bold" 
                            sx={{ 
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              color: !parent.isRegistered ? 'rgb(237, 27, 27)' : 'black',
                              lineHeight: 1.2, fontSize:'19px'
                            }}
                          >
                            {parent.name}
                          </Typography>

                          <Typography 
                            variant="body2" 
                            sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '19px' }}
                          >
                            {parent.phone}
                          </Typography>
                        </Box>
                      </Box>

                      <Box display="flex" alignItems="center">
                        <IconButton 
                          size="large" 
                          color="primary" 
                          href={`tel:${parent.phone}`} 
                          onClick={(e) => e.stopPropagation()} 
                          sx={{ padding: '6px', ml: 1 }}
                        >
                          <PhoneIcon fontSize="small" sx={{ color: 'rgb(0, 0, 0)' }} />
                        </IconButton>
                        
                        {parent.isRegistered && hasDetails && (
                          <IconButton
                            size="large"
                            onClick={() => handleToggleExpand(parent.id)}
                            sx={{ padding: '6px', ml: 0.5 }}
                          >
                            <ExpandMoreIcon 
                              fontSize="small"
                              sx={{ 
                                color: 'rgb(0, 0, 0)',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s ease-in-out'
                              }} 
                            />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    {parent.isRegistered && hasDetails && (
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ mt: 1, pt: 1, marginLeft: '42px', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {parent.studentName && (
                            <Typography variant="body2" sx={{ fontSize: '17px', color: 'text.primary' }}>
                              <strong>Student:</strong> {parent.studentName}
                            </Typography>
                          )}
                          {parent.address && (
                            <Typography variant="body2" sx={{ fontSize: '17px', color: 'text.secondary' }}>
                              <strong>Address:</strong> {parent.address}
                            </Typography>
                          )}
                          {parent.pincode && (
                            <Typography variant="body2" sx={{ fontSize: '17px', color: 'text.secondary' }}>
                              <strong>Pincode:</strong> {parent.pincode}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </Box>

        <AddParentModal 
          open={openModal} 
          onClose={() => setOpenModal(false)} 
          onAdd={handleAddParent} 
          contacts={deviceContacts} 
        />

        <FooterLogo />
      </Container>

      <Fab
        variant="extended"
        onClick={handleOpenModal}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          bgcolor: 'linear-gradient(360deg, rgb(211, 211, 211))',
          color: 'black',
          fontWeight: 'bold',
          marginBottom: '69px',
          boxShadow: '0px 6px 12px rgba(0,0,0,0.4), inset 0px 2px 1px rgba(255,255,255,0.4), inset 0px -4px 1px rgba(0,0,0,0.2)',
          textTransform: 'none',
          zIndex: 1000,
          '&:hover': {
            bgcolor: '#757575',
            boxShadow: '0px 8px 16px rgba(0,0,0,0.5), inset 0px 2px 1px rgba(255,255,255,0.4), inset 0px -4px 1px rgba(0,0,0,0.2)',
          }
        }}
      >
        <PersonAddIcon sx={{ mr: 1, color: 'black' }} />
        Parents
      </Fab>

    </Box>
  );
};

export default ParentsPage;